import { threadService } from '../api/threadService';

// Simple in-memory cache for short-lived resolutions
type CacheEntry = { id: string | null; ts: number };
const cache = new Map<string, CacheEntry>();
const TTL_MS = 10_000; // 10s

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    // hash * 33 + c
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    // Convert to 32bit int
    hash = hash | 0;
  }
  return hash >>> 0;
}

export function normalizeText(s?: string): string {
  return (s || '').replace(/\s+/g, ' ').trim();
}

/**
 * Resolve a UI assistant message (by content) to a server thread message UUID.
 * - Prefers the most recent assistant message whose normalized text equals or
 *   contains the candidate text (or vice versa) to handle minor formatting diffs.
 * - Uses a tiny TTL cache to avoid repeated thread view fetches.
 */
export async function resolveThreadMessageId(
  mainThreadId: string,
  candidateText: string,
  opts?: { role?: 'assistant' | 'user' | 'system' }
): Promise<string | null> {
  if (!mainThreadId || !candidateText) return null;
  const role = opts?.role || 'assistant';
  const key = `${mainThreadId}|${djb2(candidateText)}`;
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && (now - hit.ts) < TTL_MS) return hit.id;

  try {
    const view = await threadService.getThreadView(mainThreadId);
    const msgs = Array.isArray(view?.messages) ? view.messages : [];
    const target = normalizeText(candidateText);
    let found: string | null = null;
    for (let i = msgs.length - 1; i >= 0; i--) {
      const vm: any = msgs[i];
      if (role && vm?.role !== role) continue;
      const vmText = normalizeText((vm?.content && (vm as any).content?.text) || '');
      if (!vmText || !target) continue;
      if (vmText === target || vmText.includes(target) || target.includes(vmText)) {
        found = String(vm.id);
        break;
      }
    }
    cache.set(key, { id: found, ts: now });
    return found;
  } catch {
    cache.set(key, { id: null, ts: now });
    return null;
  }
}

